import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Rocket,
  Target,
  Users,
  Gift,
  ShieldCheck,
  TrendingUp,
  Zap,
  BadgeCheck,
  Flame,
  Sparkles,
  Wallet,
  Receipt,
  CreditCard,
  RotateCcw,

} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { WaitlistForm } from "@/components/waitlist-form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import fundHero from "@/assets/fund-hero.jpg";
import fundProjects from "@/assets/fund-projects.jpg";
import fundRewards from "@/assets/fund-rewards.jpg";
import { FUND_OG_IMAGE, FUND_OG_IMAGE_ALT, SITE_NAME, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/fund")({
  head: () =>
    buildPageMetadata({
      path: "/fund",
      title: "Crowdfunding for Creators & Startups — FUND | BeHot.Now",
      description:
        "BeHot.Now FUND is crowdfunding for creators, artists and startups: launch a project page, set a funding goal and reward tiers, collect pledges with milestone escrow. Join the waitlist.",
      ogTitle: "FUND — crowdfunding for creators, artists and startups",
      ogDescription:
        "Launch a crowdfunding campaign inside BeHot.Now: project page, funding goal, reward tiers, backer updates and milestone escrow. Coming soon — join the waitlist.",
      keywords: [
        "crowdfunding",
        "crowdfunding platform",
        "crowdfunding for creators",
        "crowdfunding for startups",
        "back a project",
        "kickstarter alternative",
        "reward tiers crowdfunding",
        "raise funds online",
        "creator funding",
        "project funding platform",
        "how does crowdfunding work",
        "BeHot.Now FUND",
      ],
      image: FUND_OG_IMAGE,
      imageAlt: FUND_OG_IMAGE_ALT,
      imageWidth: "1600",
      imageHeight: "900",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Fund", path: "/fund" },
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "BeHot.Now project crowdfunding",
          alternateName: "BeHot.Now FUND",
          serviceType: "Crowdfunding and project backing for creators, startups and artists",
          description:
            "Crowdfunding inside BeHot.Now: project pages, funding goals, reward tiers, backer updates and milestone-based escrow.",
          url: `${SITE_URL}/fund`,
          image: FUND_OG_IMAGE,
          areaServed: "Worldwide",
          provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        },
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "FUND — crowdfunding for creators, artists and startups",
          url: `${SITE_URL}/fund`,
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
          primaryImageOfPage: { "@type": "ImageObject", url: FUND_OG_IMAGE },
        },
      ],
    }),
  component: Fund,
});

const FEE_ICONS = [Receipt, CreditCard, RotateCcw];
const FEATURE_ICONS = [Rocket, Target, Users, Gift, ShieldCheck, TrendingUp];
const STEP_COUNT = 4;
const PERK_ICONS = [Zap, BadgeCheck, Wallet];
const FAQ_KEYS = ["a", "b", "c", "d", "e", "f", "g"];

function Fund() {
  const { t } = useT();

  const highlights = [
    { k: t("marketing.fund.hl1_k"), v: t("marketing.fund.hl1_v") },
    { k: t("marketing.fund.hl2_k"), v: t("marketing.fund.hl2_v") },
    { k: t("marketing.fund.hl3_k"), v: t("marketing.fund.hl3_v") },
  ];

  const fees = FEE_ICONS.map((icon, i) => ({
    icon,
    title: t(`marketing.fund.fee.${i}.title`),
    body: t(`marketing.fund.fee.${i}.body`),
  }));

  const features = FEATURE_ICONS.map((icon, i) => ({
    icon,
    title: t(`marketing.fund.feature.${i}.title`),
    body: t(`marketing.fund.feature.${i}.body`),
  }));

  const steps = Array.from({ length: STEP_COUNT }, (_, i) => ({
    title: t(`marketing.fund.step.${i}.title`),
    body: t(`marketing.fund.step.${i}.body`),
  }));

  const backerPerks = PERK_ICONS.map((icon, i) => ({
    icon,
    title: t(`marketing.fund.perk.${i}.title`),
    body: t(`marketing.fund.perk.${i}.body`),
  }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-12">
      <section className="card-surface overflow-hidden p-0">
        <div className="grid items-center gap-6 md:grid-cols-[1.1fr_1fr]">
          <div className="p-8 sm:p-10">
            <Badge variant="secondary" className="uppercase tracking-widest">{t("marketing.fund.badge_soon")}</Badge>
            <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">
              {t("marketing.fund.h1_pre")} <span className="text-gradient-hot">{t("marketing.fund.h1_grad")}</span>
            </h1>
            <p className="mt-3 text-muted-foreground">
              {t("marketing.fund.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="hot">
                <Link to="/get-hot" search={{ listing: undefined, category: undefined }}>
                  <Flame className="size-4" /> {t("marketing.fund.cta_board")}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/how-it-works">{t("marketing.fund.cta_how")}</Link>
              </Button>
            </div>
          </div>
          <img
            src={fundHero}
            alt={t("marketing.fund.hero_alt")}
            width={1600}
            height={900}
            className="h-full w-full object-cover"
          />
        </div>
      </section>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {highlights.map((x) => (
          <div key={x.v} className="card-surface p-5">
            <p className="font-display text-2xl font-bold text-gradient-hot">{x.k}</p>
            <p className="text-sm text-muted-foreground">{x.v}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="card-surface overflow-hidden">
          <img
            src={fundProjects}
            alt={t("marketing.fund.projects_alt")}
            loading="lazy"
            width={1200}
            height={800}
            className="h-48 w-full object-cover"
          />
          <div className="p-6">
            <h3 className="font-display text-lg font-bold">{t("marketing.fund.creators_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("marketing.fund.creators_body")}
            </p>
          </div>
        </div>
        <div className="card-surface overflow-hidden">
          <img
            src={fundRewards}
            alt={t("marketing.fund.rewards_alt")}
            loading="lazy"
            width={1200}
            height={800}
            className="h-48 w-full object-cover"
          />
          <div className="p-6">
            <h3 className="font-display text-lg font-bold">{t("marketing.fund.backers_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("marketing.fund.backers_body")}
            </p>
          </div>
        </div>
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.fund.features_heading")}</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {features.map((f) => (
          <div key={f.title} className="card-surface p-6">
            <div className="flex items-center justify-between gap-3">
              <f.icon className="size-6 text-creative" />
              <Badge variant="outline" className="text-[11px]">{t("marketing.fund.badge_soon")}</Badge>
            </div>
            <h3 className="mt-3 font-display text-lg font-bold">{f.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.fund.steps_heading")}</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <div key={s.title} className="card-surface p-5">
            <div className="rank-numeral text-2xl leading-none">{String(i + 1).padStart(2, "0")}</div>
            <p className="mt-2 font-display font-semibold">{s.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.fund.fees_heading")}</h2>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        {t("marketing.fund.fees_intro")}
      </p>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {fees.map((f) => (
          <div key={f.title} className="card-surface p-6">
            <f.icon className="size-6 text-primary" />
            <h3 className="mt-3 font-display text-lg font-bold">{f.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        {t("marketing.fund.fees_footnote")}
      </p>


      <div className="card-surface mt-14 p-8">
        <div className="flex flex-wrap items-center gap-3">
          <Sparkles className="size-6 text-success" />
          <h2 className="font-display text-2xl font-bold">
            {t("marketing.fund.perks_heading_pre")} <span className="text-gradient-hot">{t("marketing.fund.perks_heading_grad")}</span>
          </h2>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {t("marketing.fund.perks_intro")}
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {backerPerks.map((p) => (
            <div key={p.title} className="rounded-xl border border-border bg-background p-5">
              <p.icon className="size-6 text-primary" />
              <p className="mt-2 font-display font-semibold">{p.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{p.body}</p>
            </div>
          ))}
        </div>
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.fund.faq_heading")}</h2>
      <Accordion type="single" collapsible className="mt-4">
        {FAQ_KEYS.map((k) => (
          <AccordionItem value={k} key={k}>
            <AccordionTrigger>{t(`marketing.fund.faq.${k}.q`)}</AccordionTrigger>
            <AccordionContent>
              {t(`marketing.fund.faq.${k}.a`)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="card-surface mt-14 p-8 text-center">
        <Rocket className="mx-auto size-8 text-primary" />
        <h2 className="mt-3 font-display text-2xl font-bold">{t("marketing.fund.final_heading")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("marketing.fund.final_body")}
        </p>
        <WaitlistForm product="fund" cta={t("marketing.fund.waitlist_cta")} />
      </div>
    </div>
  );
}
